import { bg, cn } from '@/styles'
import { useEffect, useMemo, useState } from 'react'
import { InputGroup, InputGroupAddon, InputGroupInput, InputGroupText } from '@/components/ui/input-group'
import AccountIcon from './assets/account.svg'
import PasswordIcon from './assets/password.svg'
import GoogleIcon from './assets/google.svg'
import MobileIcon from './assets/mobile.svg'
import CodeIcon from './assets/code.svg'
import { useOnLogin } from '@/hooks/login/useOnLogin'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import { LoginMode, LoginType } from '@/hooks/login/constant'
import { useValidCode } from '@/hooks/login/useValidCode'
import { useCommon } from '@/core/useCommon'
import Countdown from './Countdown'
import LanguageDropdown from '@/views/LanguageItem/LanguageDropdown'
import styles from './index.module.scss'
import clsx from 'clsx'
import CheckboxIcon from './assets/checkbox.svg'
import CheckboxCheckedIcon from './assets/checkbox-checked.svg'
import { clearLoading } from '@/ui/useToastStore'
import { getPlatformLogo } from '@/env/platform'

const imageMap = new Map<LoginType, React.ReactNode>([
  [LoginType.ACCOUNT, <AccountIcon key={LoginType.ACCOUNT} className="h-24 w-24" />],
  [LoginType.PASSWORD, <PasswordIcon key={LoginType.PASSWORD} className="h-24 w-24" />],
  [LoginType.GOOGLE_VALID_CODE, <GoogleIcon key={LoginType.GOOGLE_VALID_CODE} className="h-24 w-24" />],
  [LoginType.TELEPHONE, <MobileIcon key={LoginType.TELEPHONE} className="h-24 w-24" />],
  [LoginType.VALID_CODE, <CodeIcon key={LoginType.VALID_CODE} className="h-24 w-24" />],
])

const Login = () => {
  const { t } = useCommon()
  const {
    onSubmit, // 提交
    fields, // 輸入欄位
    form: {
      register,
      formState: { errors, isSubmitting },
      watch,
    },
    activeTab, // 當前 tab
    setActiveTab, // 設定 tab
    remember, // 是否記住密碼
    setRemember, // 設定記住密碼
    sendValidCode, // 發送驗證碼
  } = useOnLogin()

  const [isSendCode, setIsSendCode] = useState(false)
  const { query } = useValidCode()
  const telephone = watch(LoginType.TELEPHONE)

  const tabOptions: OptionItem[] = useMemo(() => {
    return [
      {
        label: t('old:accountLogin'),
        value: LoginMode.ACCOUNT,
      },
      {
        label: t('old:phoneLogin'),
        value: LoginMode.MOBILE,
      },
    ]
  }, [t])

  // 不能發送驗證碼條件
  // 不 useMemo，這裡每次都會重新計算且負擔小
  const cannotSendCode = !!errors[LoginType.TELEPHONE] || !telephone || !/^09\d{9}$/.test(telephone) // 條件要和驗證規則一致

  const CheckBox = remember ? CheckboxCheckedIcon : CheckboxIcon

  // 發送驗證碼
  const handleSendCode = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation()
    if (cannotSendCode) return
    setIsSendCode(true)
    sendValidCode()
  }

  useEffect(() => {
    clearLoading()
  }, [])

  return (
    <div className={clsx('bg-full relative flex h-full w-full items-center justify-center', styles.login)}>
      <div className="rounded-24 relative flex min-h-344 w-342 flex-col border border-[#F5F5F5] bg-white/60 px-16 pt-32 pb-24 sm:min-h-596 sm:w-486 sm:border-0 sm:px-68 sm:pt-60 sm:pb-68">
        {/* 選擇語系 */}
        <LanguageDropdown />

        {/* logo 圖 */}
        <div className="flex w-full justify-center sm:block">
          <div className="bg-full h-48 w-116 sm:h-67 sm:w-165" style={bg(query.data?.t?.logoUrl || getPlatformLogo())} />
        </div>

        {/* tab */}
        <div className="text-14 sm:text-20 mt-24 flex w-full justify-between space-x-20 leading-24 font-medium select-none sm:mt-20 sm:leading-40">
          {tabOptions?.map((item) => (
            <div key={item.value} className={`cursor-pointer capitalize ${activeTab === item.value ? 'text-[#0050C8]' : 'text-[#0D2245]'}`} onClick={() => setActiveTab(item.value as LoginMode)}>
              {item.label}
            </div>
          ))}
        </div>

        {/* Form */}
        <form className="mt-24 flex w-full flex-1 flex-col sm:mt-40" onSubmit={onSubmit}>
          <div className="w-full space-y-12 sm:space-y-40">
            {fields.map((field) => (
              <div
                key={field.key}
                className={cn('relative', {
                  hidden: field.hidden,
                })}>
                {/* 輸入框 */}
                <InputGroup className="pr-18">
                  <InputGroupInput aria-invalid={!!errors[field.key]} {...field} {...register(field.key)} autoComplete="off" />
                  <InputGroupAddon>
                    {imageMap.get(field.key)}

                    {/* 手機號碼區碼 */}
                    {field.key === LoginType.TELEPHONE && <span className="text-14 sm:text-16 ml-4 text-[#9DABC9] sm:ml-8">+63</span>}
                  </InputGroupAddon>

                  {/* 输入的号码不是「09」开头并且没有等于11码【获取验证码】即呈现不可点击状态。 */}
                  <InputGroupText
                    className={cn('text-12 sm:text-16 cursor-not-allowed pr-8 select-none', {
                      'text-[#025BE8]': !cannotSendCode,
                      hidden: field.key !== LoginType.VALID_CODE,
                      'hover cursor-pointer': !cannotSendCode && !isSendCode,
                    })}>
                    {isSendCode ? (
                      <Countdown callback={() => sendValidCode()} />
                    ) : (
                      <>
                        <span
                          className={cn('rounded-38 text-12 flex h-28 min-w-54 items-center justify-center bg-[#0050C8] px-12 text-white sm:hidden', {
                            'opacity-60': cannotSendCode,
                          })}
                          onClick={handleSendCode}>
                          {t('B151')}
                        </span>
                        <span className="hidden sm:inline" onClick={handleSendCode}>
                          {t('B151')}
                        </span>
                      </>
                    )}
                  </InputGroupText>
                </InputGroup>

                {/* 錯誤訊息 */}
                {errors[field.key]?.message && (
                  <p className="text-11 sm:text-12 mt-6 leading-13 text-[#FF4343] sm:absolute sm:-bottom-25 sm:left-10 sm:mt-0 sm:leading-18">{String(errors[field.key]?.message)}</p>
                )}
              </div>
            ))}
          </div>

          {/* 記憶 */}
          {activeTab === LoginMode.ACCOUNT && (
            <div className="mt-12 min-h-24 sm:mt-0">
              <div className="flex items-center">
                <div
                  onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                    // 用點擊前的狀態來設定點擊後的狀態
                    setRemember((remember) => !remember)
                  }}
                  className="block cursor-pointer">
                  <CheckBox className="size-24" />
                </div>

                <span className="text-12 sm:text-14 ml-4 text-[#6D7693] select-none sm:ml-10">{t('old:remember')}</span>
              </div>
            </div>
          )}

          <div className="flex-1" />

          {/* 提交按鈕 */}
          <div className="mt-24 w-full sm:mt-40">
            <Button disabled={isSubmitting} type="submit">
              {t('old:login')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default Login
